"use client";

import { useEffect } from "react";

import {
  AnalyticsProvider as BaseAnalyticsProvider,
  initAnalytics,
  useAnalytics,
} from "@vllnt/analytics/react";
import { CookieConsent } from "@vllnt/ui";
import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";

const IS_DEV =
  typeof window !== "undefined" && window.location.hostname === "localhost";

const ANALYTICS_CONFIG = {
  app: "bntvllnt",
  debug: IS_DEV,
  version: 1,
};

/**
 * Cookie consent banner with analytics integration
 * Hidden on privacy page since user can manage preferences there
 * Fully localized using next-intl
 */
function ConsentBanner() {
  const { acceptAll, declineAnalytics, hasResponded, isLoading } =
    useAnalytics();
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations("privacy.banner");

  // Initialize analytics after user responds
  // oxlint-disable-next-line react-doctor/no-effect-event-handler -- hasResponded is owned by AnalyticsProvider context; can't move init into accept/decline handlers without restructuring shared state.
  useEffect(() => {
    if (hasResponded) {
      initAnalytics(ANALYTICS_CONFIG);
    }
  }, [hasResponded]);

  // Hide banner on privacy page - user can manage preferences there
  const isPrivacyPage = pathname?.includes("/privacy");

  if (isLoading || hasResponded || isPrivacyPage) return null;

  return (
    <CookieConsent
      acceptText={t("accept")}
      declineText={t("decline")}
      message={t("message")}
      onAccept={acceptAll}
      onDecline={declineAnalytics}
      open={!hasResponded}
      position="bottom-left"
      settingsHref={`/${locale}/privacy`}
      settingsText={t("privacy_policy")}
    />
  );
}

/**
 * Main analytics provider component.
 *
 * Vercel Analytics + Speed Insights removed (ADR-014 sovereignty: no
 * third-party trackers). @vllnt/analytics handles privacy-respecting
 * first-party event tracking with consent.
 *
 * Performance & SEO notes:
 * - Children render immediately (no blocking)
 * - Cookie banner is client-only, doesn't affect SSR/SEO
 */
export function AnalyticsProvider({
  children,
}: {
  children?: React.ReactNode;
}) {
  return (
    <BaseAnalyticsProvider config={ANALYTICS_CONFIG}>
      {children}
      <ConsentBanner />
    </BaseAnalyticsProvider>
  );
}

/**
 * Hook to use analytics in components
 * Re-exports useAnalytics from @vllnt/analytics for convenience
 */
export { useAnalytics } from "@vllnt/analytics/react";
